import { reaction } from 'mobx';
import { component, initialize, inject } from 'tsdi';
import { wrapRequest } from 'wrap-request';

import { Api } from './api';
import { CentralLegalLinkDto } from './api/dtos';
import { UrlStore } from './url-store';

/**
 * Extract the ISO 3166-1 alpha-2 country component from a BCP 47-style
 * locale tag. Supports both `-` and `_` separators and script-qualified
 * tags such as `zh-Hans-DE` (returns `DE`).
 */
export function extractCountryFromLocale(
    locale: string | undefined | null
): string | undefined {
    if (!locale) {
        return undefined;
    }
    const segments = locale.split(/[_-]/);
    for (let i = segments.length - 1; i >= 1; i--) {
        const segment = segments[i];
        if (/^[A-Za-z]{2}$/.test(segment)) {
            return segment.toUpperCase();
        }
    }
    return undefined;
}

const magicLineLegalLinks: CentralLegalLinkDto[] = [
    {
        centralLegalLinkDocumentType: 'IMPRINT',
        legalLinkType: 'EXTERNAL_URL',
        content: 'https://www.magicline.com/de/impressum/'
    },
    {
        centralLegalLinkDocumentType: 'PRIVACY',
        content: 'https://public.sportalliance.com/magicline/de/privacy',
        legalLinkType: 'EXTERNAL_URL'
    }
];

@component
export class StudioStore {
    @inject
    private urlStore!: UrlStore;
    @inject
    private api!: Api;

    public legalLinks = wrapRequest(
        async (studio: number) => {
            const { legalLinks } =
                await this.api.client.StudioControllerV2.getStudioLegalLinks(
                    studio
                );
            if (!legalLinks || legalLinks.length === 0) {
                return magicLineLegalLinks;
            }
            return legalLinks;
        },
        { defaultData: magicLineLegalLinks }
    );

    public studioInfo = wrapRequest((studio: number) =>
        this.api.client.StudioControllerV2.getStudio(studio)
    );

    public studioCountryRequest = wrapRequest((studio: number) =>
        this.api.client.PublicStudioController.getStudioCountry(studio)
    );

    public get studioCountry() {
        const studioCountry = this.studioCountryRequest.$?.countryCodeAlpha2;
        const localeCountryAsFallback = extractCountryFromLocale(
            this.urlStore.locale
        );
        return studioCountry ?? localeCountryAsFallback;
    }

    @initialize
    public async init() {
        reaction(
            () => this.urlStore.studio,
            (studio) => {
                if (!studio) {
                    return;
                }

                const studioId = parseInt(studio, 10);
                this.legalLinks.request(studioId);
                this.studioInfo.request(studioId);
                this.studioCountryRequest.request(studioId);
            },
            { fireImmediately: true }
        );
    }
}
